import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RepriseBrand, RepriseChoice, REPRISE_TEXT, REPRISE_DIMS, REPRISE_BEST } from "@/components/RepriseBrand";
import { useI18n } from "@/context/I18nContext";
import { extrasFor, stateLabel, stateOptions } from "@/lib/htp";
import { euroInt } from "@/lib/format";

const TextField = ({ k, lbl, ti, i, set, required, unit }) => {
  const { t } = useI18n();
  const numeric = ["ptac", "annee", "longueur_int", "largeur_int", "hauteur_int"].includes(k);
  return (
    <div><Label className="text-[11px] text-muted-foreground">{t(lbl)}{required && <span className="text-destructive"> *</span>} :</Label>
      <Input value={ti[k] || ""} onChange={(e) => set(i, k, e.target.value)} placeholder={unit || ""} inputMode={numeric ? "numeric" : undefined}
        maxLength={k === "annee" ? 4 : undefined} data-testid={`reprise-${i}-${k}`} className="h-8 text-xs mt-0.5" /></div>
  );
};

// Trade-in criteria aligned with Horse Trailer Price (same keys and value labels)
export const RepriseHtpFields = ({ ti, i, crit, setReprise, patchReprise }) => {
  const { t } = useI18n();
  const type = ti.type;
  const htpType = crit.types.includes(type);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-2 gap-y-1.5">
        <div><Label className="text-[11px] text-muted-foreground">{t("Marque")} <span className="text-destructive">*</span> :</Label>
          <RepriseBrand type={type} list={htpType ? crit.brands[type] : undefined} value={ti.marque} other={!!ti.marqueAutre} onChange={(p) => patchReprise(i, p)} testid={`reprise-${i}-marque`} />
        </div>
        {type === "van" && <RepriseChoice label="Nbre de places" options={crit.places} value={ti.places} onChange={(v) => setReprise(i, "places", v)} testid={`reprise-${i}-places`} required />}
        {REPRISE_TEXT.map(([k, lbl]) => <TextField key={k} k={k} lbl={lbl} ti={ti} i={i} set={setReprise} required={k === "annee"} unit={k === "ptac" ? "kg" : k === "annee" ? "AAAA" : ""} />)}
        {htpType && ti.type !== "van" && <RepriseChoice label="Essieux" options={crit.essieux} value={ti.essieux} onChange={(v) => setReprise(i, "essieux", v)} testid={`reprise-${i}-essieux`} />}
        {type === "fourgon" && REPRISE_DIMS.map(([k, lbl]) => <TextField key={k} k={k} lbl={lbl} ti={ti} i={i} set={setReprise} unit="cm" />)}
        {htpType && Object.keys(crit.states[type] || {}).map((k) => (
          <RepriseChoice key={k} label={stateLabel(k)} options={stateOptions(crit, type, k, ti)} value={ti[k]}
            onChange={(v) => setReprise(i, k, v)} testid={`reprise-${i}-${k}`}
            placeholder={REPRISE_BEST[k] ? `${t(REPRISE_BEST[k])} (${t("par défaut")})` : t("Choisir…")} />
        ))}
      </div>
      {htpType && extrasFor(crit, type).some((e) => !!ti[e.key]) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid={`reprise-${i}-extras`}>
          <span className="text-[11px] text-muted-foreground">{t("Options")} :</span>
          {extrasFor(crit, type).filter((e) => !!ti[e.key]).map((e) => (
            <label key={e.key} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`reprise-${i}-extra-${e.key}`}>
              <Checkbox checked={!!ti[e.key]} onCheckedChange={(v) => setReprise(i, e.key, v ? "1" : "")} />{t(e.label)}
            </label>
          ))}
        </div>
      )}
    </>
  );
};

export const HtpAmounts = ({ htp, i }) => {
  const { t } = useI18n();
  if (!htp) return null;
  const rows = [["Reprise TTC", htp.montant_ttc], ["Reprise HT", htp.prix_reprise_ht], ["Prix revendeur TTC", htp.prix_revendeur_ttc], ["Prix client TTC", htp.prix_client_ttc], ["Prix proposé par le vendeur", htp.prix_saisi]].filter(([, v]) => v > 0);
  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs space-y-1" data-testid={`reprise-${i}-htp-amounts`}>
      <div className="font-semibold text-primary">{t("Estimation Horse Trailer Price")}{htp.estimation_id ? ` n° ${htp.estimation_id}` : ""}</div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {rows.map(([l, v]) => <span key={l}>{t(l)} : <b className="font-mono">{euroInt(v)}</b></span>)}
        {!rows.length && <span className="text-muted-foreground">{t("Aucun montant transmis")}</span>}
      </div>
    </div>
  );
};
